import { useCallback, useEffect, useRef } from 'react';

export interface UseDelayedActionResult {
  /** Runs `action` after the delay, replacing any action still pending. */
  schedule: (action: () => void) => void;
  /** Drops the pending action, if any. */
  cancel: () => void;
}

/**
 * One pending action at a time, run after `delayMs`. The pending action is
 * dropped on unmount, so it never fires into an unmounted component.
 */
export const useDelayedAction = (delayMs: number): UseDelayedActionResult => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const schedule = useCallback(
    (action: () => void) => {
      cancel();
      timer.current = setTimeout(() => {
        timer.current = null;
        action();
      }, delayMs);
    },
    [cancel, delayMs]
  );

  useEffect(() => cancel, [cancel]);

  return { schedule, cancel };
};
